// Re-export the generated types from the API library
export type { UsersUserResponseDto as User } from "@o2-fe/api";
export type { UsersAddressDto as Address } from "@o2-fe/api";

// Extract the gender and role types from the generated DTO
export type GenderType = "MALE" | "FEMALE" | "OTHER";

/**
 * User roles enum
 *
 * ## Role Types
 *
 * ### Hierarchical Roles (Primary organizational roles)
 * These represent the user's position in the company hierarchy:
 * - ADMIN (5): Full system access
 * - PROJECT_MANAGER (4): Manages projects and teams
 * - SALES (3): Sales operations
 * - TEAM_LEAD (2): Team coordination
 * - EMPLOYEE (1): Standard employee
 * - EXTERNAL_COLLABORATOR (0.5): External collaborator
 *
 * ### Complementary Roles (Aggregated functional roles)
 * These are functional roles that can be combined with hierarchical roles:
 * - APPROVER (0): Can approve/clock subtasks (timbratura)
 * - VALIDATOR (0): Can validate subtasks
 *
 * ## Permission Model
 * - Hierarchical roles determine base permissions (higher level = more permissions)
 * - Complementary roles ADD specific permissions to the user's base role
 * - A user with multiple roles (e.g., EMPLOYEE + VALIDATOR) gets:
 *   - Permissions from the highest hierarchical role (EMPLOYEE)
 *   - Additional permissions from complementary roles (validation)
 *   - Combined data visibility from all roles
 */
export enum UserRole {
  // Hierarchical roles (ordered by level)
  ADMIN = "ADMIN",
  PROJECT_MANAGER = "PROJECT_MANAGER",
  SALES = "SALES",
  TEAM_LEAD = "TEAM_LEAD",
  ADMINISTRATION = "ADMINISTRATION",
  EMPLOYEE = "EMPLOYEE",
  EXTERNAL_COLLABORATOR = "EXTERNAL_COLLABORATOR",

  // Complementary/aggregated roles (level 0)
  APPROVER = "APPROVER",
  VALIDATOR = "VALIDATOR",
}

/**
 * User status enum
 * Represents the lifecycle states of a user account
 */
export enum UserStatus {
  /** User account is active and can access the system */
  ACTIVE = "ACTIVE",
  /** User account is disabled and cannot access the system */
  INACTIVE = "INACTIVE",
  /** User is in trial setup phase, needs to complete profile */
  TRIAL_SETUP = "TRIAL_SETUP",
}

/**
 * Type alias for API status string literals
 */
export type UserStatusString = "ACTIVE" | "INACTIVE" | "TRIAL_SETUP";

/**
 * Check if user status is active
 * @param status - User status (enum or string literal)
 * @returns true if user is active
 */
export function isUserActive(status: UserStatus | UserStatusString): boolean {
  return status === UserStatus.ACTIVE;
}

/**
 * Check if user is in trial setup
 * @param status - User status (enum or string literal)
 * @returns true if user is in trial setup
 */
export function isUserInTrialSetup(
  status: UserStatus | UserStatusString,
): boolean {
  return status === UserStatus.TRIAL_SETUP;
}

/**
 * Check if user account is deactivated
 * @param status - User status (enum or string literal)
 * @returns true if user is inactive
 */
export function isUserInactive(
  status: UserStatus | UserStatusString,
): boolean {
  return status === UserStatus.INACTIVE;
}

/**
 * Role hierarchy - higher values have more permissions
 * Used for determining the highest role when a user has multiple roles
 *
 * Hierarchy (low to high):
 * APPROVER/VALIDATOR (0) < EXTERNAL_COLLABORATOR (0.5) < EMPLOYEE (1) < TEAM_LEAD (2) < SALES (3) < PROJECT_MANAGER (4) < ADMIN (5)
 *
 * Note: APPROVER and VALIDATOR are at level 0 but are treated as "aggregated roles" in data access.
 * When combined with higher roles (e.g., PM + VALIDATOR), the user sees data from both roles.
 */
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  [UserRole.ADMIN]: 5,
  [UserRole.TEAM_LEAD]: 4,
  [UserRole.PROJECT_MANAGER]: 3,
  [UserRole.SALES]: 2,
  // Functional back-office role: above EMPLOYEE, below PM/TL/SALES so it never
  // satisfies isProjectManagerOrHigher / isTeamLeadOrHigher. Module/page access
  // is granted explicitly via the permissions config, not the hierarchy.
  [UserRole.ADMINISTRATION]: 1.5,
  [UserRole.EMPLOYEE]: 1,
  [UserRole.EXTERNAL_COLLABORATOR]: 0.5,
  [UserRole.APPROVER]: 0,
  [UserRole.VALIDATOR]: 0,
};

/**
 * Get the highest role from a list of roles based on hierarchy
 * @param roles - Array of user roles
 * @returns The highest role, or EMPLOYEE if array is empty
 */
export function getHighestRole(roles: UserRole[]): UserRole {
  if (!roles || roles.length === 0) {
    return UserRole.EMPLOYEE;
  }

  return roles.reduce((highest, current) => {
    const currentLevel = ROLE_HIERARCHY[current] || 0;
    const highestLevel = ROLE_HIERARCHY[highest] || 0;
    return currentLevel > highestLevel ? current : highest;
  }, roles[0]);
}

/**
 * Check if a user is an external collaborator based on their highest role
 * @param roles - Array of user roles
 * @returns true if the highest role is EXTERNAL_COLLABORATOR, false otherwise
 */
export function isExternalCollaborator(roles: UserRole[]): boolean {
  const highestRole = getHighestRole(roles);
  return highestRole === UserRole.EXTERNAL_COLLABORATOR;
}
